import { DateTime } from 'luxon';
import { Log_Workout } from '@cf-shared/data-model/log-workout';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordState } from '@cf-shared/data-model/user-log';
import { WorkoutLoadMode } from 'models/workout-load-mode';

/**
 * How a workout's mode is decided, kept apart from `WorkoutLoaderService`.
 *
 * The service method that answers this question reads the database, so nothing
 * could drive the decision from a test. The decision itself needs no database
 * at all once the surrounding workouts have been fetched, so it lives here, in
 * a plain module a mocha unit test can load — the same reason
 * `models/workout-load-mode.ts` holds the enum apart from the service.
 *
 * `WorkoutLoaderService.getWorkoutModeWithContextAsync` is the only caller. It
 * keeps the one thing that genuinely needs the database: working out which days
 * to ask for, and asking for them.
 */

/** What a mode decision says, including who the current workout turned out to be. */
export interface WorkoutLoadModeResult {
  mode: WorkoutLoadMode;
  /** the workout the user is meant to be doing now, when this one is ahead of it */
  currentWorkout?: Log_Workout;
  /** the workouts between the current one and this one, when this one is ahead of it */
  workoutsBetween?: Log_Workout[];
}

/**
 * The part of the answer that can be given from this workout alone.
 *
 * Returns `undefined` when it cannot be — the caller then has to fetch the
 * workouts around this one and ask `resolveWorkoutLoadModeFromNeighbours`.
 *
 * These two checks are deliberately asked BEFORE that fetch rather than folded
 * into it. A workout that is already finished or skipped is the common case on
 * the Home page, and a page showing a week of them would pay for one database
 * read per item to reach an answer that never depended on the neighbours.
 */
export function resolveWorkoutLoadModeWithoutNeighbours(
  logWorkout: Log_Workout,
  nowLocal: DateTime,
): WorkoutLoadMode | undefined {
  // if workout is already done or skipped, it is in the past
  if (logWorkout.state === LogRecordState.Done || logWorkout.state === LogRecordState.Skipped) {
    return WorkoutLoadMode.Past;
  }

  // if workout date is in the future, it's HardFuture
  const workoutDay = getEntryLocalTimeInDesiredTimeZone(logWorkout, nowLocal).startOf('day');
  const todayDay = nowLocal.startOf('day');
  if (workoutDay > todayDay) {
    return WorkoutLoadMode.HardFuture;
  }

  return undefined;
}

/**
 * Which workout the user is meant to be doing now, and where this one sits
 * relative to it.
 *
 * `workoutsAroundThis` is the workouts on the days around this one, in date
 * order, capped at today — what `getWorkoutModeWithContextAsync` fetches.
 *
 * The walk runs BACKWARDS from the most recent day and stops at the first
 * workout that is finished or skipped, taking the workout after it as the
 * current one. That is what puts a workout still marked in-progress into the
 * past as soon as ANY later session is completed, and nothing can bring it back
 * to current afterwards (#4960).
 */
export function resolveWorkoutLoadModeFromNeighbours(
  logWorkout: Log_Workout,
  workoutsAroundThis: readonly Log_Workout[],
  nowLocal: DateTime,
): WorkoutLoadModeResult {
  const workoutDay = getEntryLocalTimeInDesiredTimeZone(logWorkout, nowLocal).startOf('day');
  const todayDay = nowLocal.startOf('day');

  const thisWorkoutIndex = workoutsAroundThis.findIndex(w => w._id === logWorkout._id);
  if (thisWorkoutIndex < 0) {
    return { mode: WorkoutLoadMode.Past };
  }

  // find "Current" workout by going backwards
  let currentWorkoutIndex = -1;
  for (let i = workoutsAroundThis.length - 1; i >= 0; i--) {
    const w = workoutsAroundThis[i];
    if (w.state === LogRecordState.Incomplete) {
      currentWorkoutIndex = i;
      break;
    }
    const wDay = getEntryLocalTimeInDesiredTimeZone(w, nowLocal);
    if (w.state === LogRecordState.Scheduled) {
      if (wDay.hasSame(todayDay, 'day')) {
        currentWorkoutIndex = i;
        continue;
      } else {
        currentWorkoutIndex = i;
        break;
      }
    }
    if (w.state === LogRecordState.Done || w.state === LogRecordState.Skipped) {
      currentWorkoutIndex = i + 1;
      break;
    }
  }

  // if current workout not found, determine based on date
  if (currentWorkoutIndex < 0 || currentWorkoutIndex >= workoutsAroundThis.length) {
    const deltaDays = workoutDay.diff(todayDay, 'days').as('days');
    if (deltaDays === 0) return { mode: WorkoutLoadMode.Current };
    if (deltaDays < 0) return { mode: WorkoutLoadMode.Past };
    return { mode: WorkoutLoadMode.HardFuture };
  }

  // determine mode based on position relative to current
  if (thisWorkoutIndex === currentWorkoutIndex) {
    return { mode: WorkoutLoadMode.Current };
  } else if (thisWorkoutIndex < currentWorkoutIndex) {
    return { mode: WorkoutLoadMode.Past };
  } else {
    // SoftFuture - include context about current workout and workouts between
    const currentWorkout = workoutsAroundThis[currentWorkoutIndex];
    const workoutsBetween = workoutsAroundThis.slice(currentWorkoutIndex, thisWorkoutIndex);
    return { mode: WorkoutLoadMode.SoftFuture, currentWorkout, workoutsBetween };
  }
}

/**
 * May the workout page repair a workout it finds stored as unfinished when every
 * set on screen is finished with? See `repairWorkoutLeftOpenAsync` in
 * `workout-detail.ts` for what that repair does.
 *
 * This asks about the MODE only. The page adds the two guards a mode cannot
 * express: never while a coach is managing a client, and never before the
 * exercise list has loaded.
 *
 * # Why a workout in the PAST is included (#4960)
 *
 * The repair used to run only for the workout the user is meant to be doing now,
 * or one ahead of it. But finishing any later session moves an earlier one that
 * is still marked in progress behind the current workout — see
 * `resolveWorkoutLoadModeFromNeighbours` — and nothing can bring it forward
 * again. So every customer in the #4802 cluster who carried on training was left
 * with a "Resume Workout" card that no repair could ever clear, and the only way
 * out was to edit a set, which means changing a weight or rep value they had
 * logged correctly.
 *
 * What stops this closing a session the user really did abandon part-way is the
 * set list, not the mode. A set is written to the document only once it is
 * concluded, but the page's loader fills in the ones the user never reached, so
 * a half-finished exercise still shows an unreached set and the repair refuses.
 * That protection is if anything firmer for a past workout: its planned set
 * count comes from the snapshot stored with that workout (`planSubslotBackup`,
 * #3354) rather than from the live plan, so a volume change made since cannot
 * add or hide an unreached set.
 *
 * The other two repairs that run when the page loads —
 * `healOrphanedAutoregulationPending` and `healOrphanedPlateauDetected` — stay
 * on the narrower rule deliberately. They clear a card the user is meant to
 * ANSWER, which only makes sense on a workout they can still act on.
 */
export function mayRepairStuckWorkoutInMode(mode: WorkoutLoadMode): boolean {
  return mode === WorkoutLoadMode.Current
    || mode === WorkoutLoadMode.SoftFuture
    || mode === WorkoutLoadMode.Past;
}

/**
 * Whether the plan-reference repair (#5640, `healPlanReferenceContradictedByLoggedSets`) may
 * relabel a workout opened in this mode.
 *
 * Only the workout the user can train now. The repair judges a workout's logged sets against
 * the plan the user has TODAY, and only the current workout is sure to have been trained under
 * it. A past session may come from a plan the user has since changed, where the same sub-slot
 * ids sat in a different workout (#6565): one user's August "Workout C" read as "Workout A" of
 * their new plan. The #5640 case is still covered, because an unfinished workout opened the
 * next day is the newest unfinished one, which `resolveWorkoutLoadModeFromNeighbours` makes
 * the current workout.
 */
export function mayHealPlanReferenceInMode(mode: WorkoutLoadMode): boolean {
  return mode === WorkoutLoadMode.Current;
}

/**
 * What `WorkoutLoaderService.getEffectiveExercises` does with a workout's STORED exercise list
 * (#6574). A workout with no stored list is built from the plan either way, and a finished or
 * skipped one is returned as stored either way.
 */
export enum PlanMerge {
  /** merge the stored list with the current plan's workout: the plan's new exercises are added
   *  as empty entries, and stored exercises with no logged set that the plan dropped are removed */
  WithCurrentPlan = 'WithCurrentPlan',
  /** return the stored list as it is — what every reader of logged sets wants */
  AsStored = 'AsStored',
}

/**
 * Whether a workout opened in this mode may have its stored exercise list merged with the
 * CURRENT plan (#6574).
 *
 * Every mode except `Past`. A past unfinished workout is a session the user abandoned, and it
 * stays unfinished for good: merging it would add exercises from a plan the user changed
 * afterwards, which the day list then showed for that old day and a set edit on the workout
 * page saved into it. A future workout
 * that already has a stored list is included on purpose — it will be trained under today's plan,
 * so it must keep picking up plan edits, as `Current` does (#2786). `Template` and `Unknown`
 * keep the merge they always had.
 */
export function planMergeForMode(mode: WorkoutLoadMode): PlanMerge {
  return mode === WorkoutLoadMode.Past ? PlanMerge.AsStored : PlanMerge.WithCurrentPlan;
}
